import { useState } from "react";
import { Form } from "react-bootstrap";
import { Training } from "../../../../interfaces/training";

/**
 * Props for TrainingAutocompleteInput component
 */
interface TrainingAutocompleteInputProps {
    fieldType: 'type' | 'pct' | 'description';
    name: string;
    defaultValue: string;
    placeholder?: string;
    dataAttributes: {
        season: number;
        team: number;
        week: string;
        day: string;
        moment: string;
        session: string;
        field: string;
    };
    trainings: Training[];
    currentTipo?: string;
    currentPct?: string;
    onBlur: (event: any) => void;
    onSelectTraining: (training: Training, shouldFillAllFields: boolean) => void;
    ariaLabel: string;
    isSelectingTraining: boolean;
}

/**
 * Autocomplete input component for training fields (type, pct, description)
 * Handles filtering, suggestions display, and selection
 */
export function TrainingAutocompleteInput(props: TrainingAutocompleteInputProps) {
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [showSearchIcon, setShowSearchIcon] = useState<boolean>(false);

    /**
     * Get filtered trainings based on field type and search criteria
     */
    const getFilteredTrainings = (): Training[] => {
        let filtered = props.trainings;

        if (props.fieldType === 'type') {
            // Filter by tipo, remove duplicates
            if (searchText.length > 0) {
                filtered = filtered.filter(t =>
                    t.tipo.toLowerCase().startsWith(searchText.toLowerCase())
                );
            }

            // Remove duplicates by tipo
            const seenTipos = new Set<string>();
            return filtered.filter(t => {
                const tipoLower = t.tipo.toLowerCase();
                if (seenTipos.has(tipoLower)) {
                    return false;
                }
                seenTipos.add(tipoLower);
                return true;
            });
        }

        if (props.fieldType === 'pct') {
            // Filter by tipo first, then by pct
            if (!props.currentTipo || props.currentTipo.trim() === '') {
                return [];
            }

            filtered = filtered.filter(t =>
                t.tipo.toLowerCase() === props.currentTipo!.toLowerCase()
            );

            if (searchText.length > 0) {
                filtered = filtered.filter(t =>
                    t.carga && t.carga.toString().startsWith(searchText)
                );
            }

            // Remove duplicates by tipo + carga + descripción
            const seenCombinations = new Set<string>();
            return filtered.filter(t => {
                const combination = `${t.tipo}_${t.carga}_${t.descripcion || ''}`;
                if (seenCombinations.has(combination)) {
                    return false;
                }
                seenCombinations.add(combination);
                return true;
            });
        }

        if (props.fieldType === 'description') {
            // Filter by tipo and pct, then by description
            if (!props.currentTipo || props.currentTipo.trim() === '') {
                return [];
            }

            filtered = filtered.filter(t =>
                t.tipo.toLowerCase() === props.currentTipo!.toLowerCase()
            );

            if (props.currentPct && props.currentPct.trim() !== '') {
                filtered = filtered.filter(t =>
                    t.carga && t.carga.toString() === props.currentPct
                );
            }

            if (searchText.length > 0) {
                filtered = filtered.filter(t =>
                    t.descripcion && t.descripcion.toLowerCase().includes(searchText.toLowerCase())
                );
            }

            return filtered;
        }

        return filtered;
    };

    /**
     * Handle input change
     */
    const handleInputChange = (event: any) => {
        const value = event.target.value;
        setSearchText(value);

        if (value.length > 0) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    /**
     * Handle input blur
     */
    const handleInputBlur = (event: any) => {
        // Close suggestions immediately when leaving the cell
        setTimeout(() => {
            setShowSuggestions(false);
            setShowSearchIcon(false);
        }, 200);

        // Only call onBlur if NOT selecting a training from autocomplete
        if (!props.isSelectingTraining) {
            props.onBlur(event);
        }
    };

    /**
     * Handle input focus
     */
    const handleInputFocus = () => {
        // Initialize searchText with current field value if not already set
        if (!searchText && props.defaultValue) {
            setSearchText(props.defaultValue);
        }
        if (props.defaultValue && props.defaultValue.length > 0) {
            setShowSuggestions(true);
        }
    };

    /**
     * Handle search icon click - show all suggestions
     */
    const handleSearchIconClick = () => {
        setSearchText('');
        setShowSuggestions(true);
    };

    /**
     * Render suggestion item based on field type
     */
    const renderSuggestion = (training: Training) => {
        if (props.fieldType === 'type') {
            return training.tipo;
        }

        if (props.fieldType === 'pct') {
            return (
                <>
                    <div><strong>{training.tipo}</strong> - {training.carga}%</div>
                    {training.descripcion && (
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                            {training.descripcion.substring(0, 40)}{training.descripcion.length > 40 ? '...' : ''}
                        </div>
                    )}
                </>
            );
        }

        if (props.fieldType === 'description') {
            return (
                <div style={{ fontSize: '0.85em', color: '#333' }}>
                    {training.descripcion}
                </div>
            );
        }
    };

    /**
     * Get dropdown width based on field type
     */
    const getDropdownWidth = (): string => {
        if (props.fieldType === 'type') return '200px';
        if (props.fieldType === 'pct') return '250px';
        if (props.fieldType === 'description') return '300px';
        return '200px';
    };

    const filteredTrainings = getFilteredTrainings();
    // Mostrar todas las opciones filtradas para todos los tipos de campo
    const limit = filteredTrainings.length;

    return (
        <div
            style={{ position: 'relative' }}
            onMouseEnter={() => props.fieldType !== 'type' && setShowSearchIcon(true)}
            onMouseLeave={() => setShowSearchIcon(false)}
        >
            {showSearchIcon && props.fieldType !== 'type' && (
                <i
                    className="bi bi-search"
                    onClick={handleSearchIconClick}
                    onMouseDown={(e) => e.preventDefault()} // Prevenir que el input pierda el foco
                    style={{
                        position: 'absolute',
                        left: '5px',
                        top: '5px',
                        cursor: 'pointer',
                        color: '#666',
                        fontSize: '14px',
                        zIndex: 10,
                        pointerEvents: 'auto'
                    }}
                    title="Mostrar todas las opciones"
                />
            )}
            <Form.Control
                type="text"
                aria-label={props.ariaLabel}
                name={props.name}
                data-season={props.dataAttributes.season}
                data-team={props.dataAttributes.team}
                data-week={props.dataAttributes.week}
                data-day={props.dataAttributes.day}
                data-moment={props.dataAttributes.moment}
                data-session={props.dataAttributes.session}
                data-field={props.dataAttributes.field}
                defaultValue={props.defaultValue}
                placeholder={props.placeholder}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={{ paddingLeft: (showSearchIcon && props.fieldType !== 'type') ? '25px' : '12px' }}
            />
            {showSuggestions && filteredTrainings.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    minWidth: getDropdownWidth(),
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    {filteredTrainings.slice(0, limit).map(training => (
                        <div
                            key={training.id}
                            onMouseDown={() => {
                                // Solo rellenar todos los campos si se selecciona desde % o descripción
                                const shouldFillAllFields = props.fieldType === 'pct' || props.fieldType === 'description';
                                props.onSelectTraining(training, shouldFillAllFields);
                            }}
                            style={{
                                padding: '8px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                fontSize: '0.9em'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                            {renderSuggestion(training)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
